// App shell and tiny router. Owned by the spec; the FRONTEND agent may extend it (e.g. a global call bar),
// but page bodies live in web/src/pages/*.
import { useEffect, useState } from 'preact/hooks';
import type { ComponentType } from 'preact';
import type { Me } from '../../src/shared/api';
import { api } from './api';
import { MeContext } from './me';
import { Toasts } from './components/ui';
import { PhoneProvider } from './phone/PhoneProvider';
import { phone } from './phone/manager';
import { Login } from './pages/Login';
import { Dialer } from './pages/Dialer';
import { MissedCalls } from './pages/MissedCalls';
import { Campaigns } from './pages/Campaigns';
import { Leads } from './pages/Leads';
import { Tasks } from './pages/Tasks';
import { Texts } from './pages/Texts';
import { Meetings } from './pages/Meetings';
import { BookedCalls } from './pages/BookedCalls';
import { Reports } from './pages/Reports';
import { Numbers } from './pages/Numbers';
import { Recordings } from './pages/Recordings';
import { Settings } from './pages/Settings';
import { Training } from './pages/Training';
import { LeadSearch } from './pages/LeadSearch';
import { Approvals } from './pages/Approvals';
import { Guide } from './pages/Guide';
import { InviteAccept } from './pages/InviteAccept';
import { resetApprovals, useApprovals } from './approvals';
import { resetSmsUnread, useSmsUnread } from './texts';
import { resetMissed, useMissedCount } from './missed';

interface RouteDef { path: string; label: string; page: ComponentType<{ params: Record<string, string> }>; admin?: boolean }

export const ROUTES: RouteDef[] = [
  { path: '/', label: 'Dial', page: Dialer },
  { path: '/missed-calls', label: 'Missed calls', page: MissedCalls },  // PJ 2026-10-24: every inbound call nobody answered, to call back
  { path: '/texts', label: 'Texts', page: Texts },                    // two-way texting (owner request 2026-10-03)
  { path: '/tasks', label: 'Tasks', page: Tasks },
  { path: '/meetings', label: 'Meetings', page: Meetings },
  { path: '/booked-calls', label: 'Booked calls', page: BookedCalls },  // every booked call for team review (2026-10-06, migration 0027)
  { path: '/leads', label: 'Leads', page: Leads },
  { path: '/campaigns', label: 'Campaigns', page: Campaigns },
  { path: '/training', label: 'Training', page: Training },
  { path: '/recordings', label: 'Voicemails', page: Recordings },
  { path: '/reports', label: 'Reports', page: Reports },
  { path: '/lead-search', label: 'Lead search', page: LeadSearch },   // LEADGEN-SPEC §10; setters too since SPEC §19 (own searches, PJ approves runs)
  { path: '/approvals', label: 'Approvals', page: Approvals },        // SPEC §19: spend approvals (PJ decides; others see their own)
  { path: '/guide', label: 'Guide', page: Guide },                    // SPEC §19: how to use the dialer and the lead engine
  { path: '/numbers', label: 'Numbers', page: Numbers, admin: true },
  { path: '/settings', label: 'Settings', page: Settings, admin: true },
];

export function navigate(to: string): void {
  if (to === location.pathname + location.search) return;
  history.pushState(null, '', to);
  window.dispatchEvent(new Event('vd:navigate'));
}

function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const on = () => setPath(location.pathname);
    window.addEventListener('popstate', on);
    window.addEventListener('vd:navigate', on);
    return () => { window.removeEventListener('popstate', on); window.removeEventListener('vd:navigate', on); };
  }, []);
  return path;
}

/** '/campaigns/12' matches '/campaigns' with params.rest = '12'. */
function match(path: string): { route: RouteDef; params: Record<string, string> } {
  const exact = ROUTES.find((r) => r.path === path);
  if (exact) return { route: exact, params: {} };
  const prefix = ROUTES.filter((r) => r.path !== '/' && path.startsWith(r.path + '/')).sort((a, b) => b.path.length - a.path.length)[0];
  if (prefix) return { route: prefix, params: { rest: path.slice(prefix.path.length + 1) } };
  return { route: ROUTES[0], params: {} };
}

/** Unread texts next to "Texts" (polled every 20 s while signed in; a toast when a new one arrives: web/src/texts.ts). */
function TextsBadge() {
  const s = useSmsUnread();
  if (!s || !s.count) return null;
  return <span class="chip chip-ok" style="margin-left:6px" title="Unread texts in your conversations">{s.count}</span>;
}

/** My open missed calls next to "Missed calls" (PJ 2026-10-24; polled every 30 s: web/src/missed.ts). Red: someone is
 *  waiting for a call back. */
function MissedBadge() {
  const s = useMissedCount();
  if (!s || !s.open) return null;
  return <span class="chip chip-bad" style="margin-left:6px" title="Your missed calls nobody has called back yet">{s.open}</span>;
}

/** Pending spend requests next to "Approvals": every waiting one for the owner, your own for everyone else. */
function ApprovalsBadge() {
  const s = useApprovals();
  if (!s || !s.pending) return null;
  return <span class={s.isOwner ? 'chip chip-warn' : 'chip'} style="margin-left:6px" title={s.isOwner ? 'Requests waiting for your approval' : 'Your requests waiting for approval'}>{s.pending}</span>;
}

export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const path = usePath();

  useEffect(() => {
    api.get<Me>('/api/auth/me').then(setMe).catch(() => setMe(null));
    const onUnauth = () => setMe(null);
    window.addEventListener('vd:unauthenticated', onUnauth);
    return () => window.removeEventListener('vd:unauthenticated', onUnauth);
  }, []);

  // The emailed invite link works signed in or not (SPEC §19); accepting signs the new teammate in and opens the Guide.
  if (path === '/invite') return <><InviteAccept onSignedIn={(m) => { setMe(m); navigate('/guide'); }} /><Toasts /></>;
  if (me === undefined) return <div class="main muted">Loading…</div>;
  if (me === null) return <><Login onSignedIn={setMe} /><Toasts /></>;

  const { route, params } = match(path);
  const Page = route.admin && me.role !== 'admin' ? Dialer : route.page;
  return (
    <MeContext.Provider value={me}>
      <PhoneProvider>
        <div class="shell">
          <nav class="nav">
            <div class="brand"><img src="/brand/valistix-mark-reversed.svg" alt="" width="24" height="24" /><div>Valistix<span>.</span> Dialer</div></div>
            {ROUTES.filter((r) => !r.admin || me.role === 'admin').map((r) => (
              <a href={r.path} class={route.path === r.path ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); navigate(r.path); }}>{r.label}{r.path === '/approvals' ? <ApprovalsBadge /> : r.path === '/texts' ? <TextsBadge /> : r.path === '/missed-calls' ? <MissedBadge /> : null}</a>
            ))}
            <div class="spacer" />
            <div class="eyebrow nav-user">{me.name} · {me.role}</div>
            <a href="#" onClick={async (e) => {
              e.preventDefault();
              if (phone.hasCall() && !confirm("You're on a call. Sign out anyway? The call stays up until you hang up.")) return;
              await api.post('/api/auth/logout').catch(() => {});
              resetApprovals();
              resetSmsUnread();
              resetMissed();
              setMe(null);
            }}>Sign out</a>
          </nav>
          <main class="main"><Page params={params} /></main>
        </div>
      </PhoneProvider>
      <Toasts />
    </MeContext.Provider>
  );
}
